import { useState, useMemo, useCallback } from "react";

import { Form, message as antdMessage } from "antd";
import dayjs from "dayjs";

import { Invoice } from "../bitmaker/schemas/Billing/Invoice";

import { useApiError } from "./useApiError";

import { BitmakerAPIClient } from "../services/BitmakerApp";

import { formatCurrency } from "../utils";

type EmailTemplateParams = {
  senderName: string;
  invoiceNumber: string;
  amountDue: string;
  dueDate: string;
};

const EMAIL_TEMPLATES: Record<
  "en" | "es",
  (params: EmailTemplateParams) => { subject: string; body: string }
> = {
  en: ({
    senderName,
    invoiceNumber,
    amountDue,
    dueDate,
  }: EmailTemplateParams) => ({
    subject: `${senderName} sent you an invoice (#${invoiceNumber})`,
    body: `${senderName} sent you an invoice (#${invoiceNumber}) for ${amountDue} that's due on ${dueDate}.`,
  }),
  es: ({
    senderName,
    invoiceNumber,
    amountDue,
    dueDate,
  }: EmailTemplateParams) => ({
    subject: `${senderName} te envió una factura (#${invoiceNumber})`,
    body: `${senderName} te envió una factura (#${invoiceNumber}) por ${amountDue} con vencimiento el ${dueDate}.`,
  }),
};

export function useInvoiceEmail(invoice: Invoice | null) {
  const [isSendInvoiceModalOpen, setIsSendInvoiceModalOpen] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [sendInvoiceError, setSendInvoiceError] = useState<string | null>(null);

  const [emailForm] = Form.useForm();

  const { handleAPIError } = useApiError();

  const initialEmailValues = useMemo(() => {
    if (!invoice) return null;

    const language = invoice.language === "es" ? "es" : "en";

    const senderName =
      invoice.companyCorporateName || invoice.companyName || "Our company";
    const invoiceNumber = invoice.invoiceNumber || "N/A";
    const amountDue = invoice.totalAmount - invoice.amountPaid;
    const amountDueFormatted = formatCurrency(
      amountDue,
      invoice.currency,
      language,
    );
    const dueDateFormatted =
      language === "es"
        ? dayjs(invoice.dueDate)
          .locale(language)
          .format("DD [de] MMMM [de] YYYY")
        : dayjs(invoice.dueDate).locale(language).format("MMMM DD, YYYY");

    const template = EMAIL_TEMPLATES[language]({
      senderName,
      invoiceNumber,
      amountDue: amountDueFormatted,
      dueDate: dueDateFormatted,
    });

    return {
      to: invoice.clientEmailAddress ? [invoice.clientEmailAddress] : [],
      subject: template.subject,
      body: template.body,
    };
  }, [invoice]);

  const handleOpenSendInvoiceModal = useCallback(() => {
    if (!initialEmailValues) return;
    emailForm.setFieldsValue(initialEmailValues);
    setIsSendInvoiceModalOpen(true);
  }, [initialEmailValues, emailForm]);

  const handleCloseSendInvoiceModal = useCallback(() => {
    setIsSendInvoiceModalOpen(false);
    emailForm.resetFields();
    setSendInvoiceError(null);
  }, [emailForm]);

  const handleSendEmail = useCallback(
    (values: { to: string[]; subject: string; body: string }) => {
      if (!invoice) return;

      setIsSendingInvoice(true);
      setSendInvoiceError(null);

      const { to, subject, body } = values;
      const payload = { to, subject, body };

      BitmakerAPIClient.post(
        `/billing/invoices/${invoice.id}/email`,
        payload,
        (response) => {
          const { isError, message: apiMessage } = handleAPIError(response);

          if (isError) {
            setIsSendingInvoice(false);
            console.error("Error sending email:", apiMessage);
            setSendInvoiceError("Failed to send email. Please try again.");
            return;
          }

          antdMessage.success("Invoice email sent successfully!");
          setIsSendingInvoice(false);
          setSendInvoiceError(null);
          handleCloseSendInvoiceModal();
        },
      );
    },
    [invoice, handleAPIError, handleCloseSendInvoiceModal],
  );

  const validateEmail = useCallback((_rule: any, value: any) => {
    if (!value || value.length === 0) {
      return Promise.resolve();
    }

    const emails = Array.isArray(value)
      ? value
      : value
        .split(",")
        .map((e: string) => e.trim())
        .filter((e: string) => e.length > 0);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const email of emails) {
      if (!emailRegex.test(email)) {
        return Promise.reject(
          new Error(`"${email}" is not a valid email format.`),
        );
      }
    }

    return Promise.resolve();
  }, []);

  return {
    emailForm,
    isSendInvoiceModalOpen,
    isSendingInvoice,
    sendInvoiceError,
    handleOpenSendInvoiceModal,
    handleCloseSendInvoiceModal,
    handleSendEmail,
    validateEmail,
  };
}
